import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import ErrorModal from '@/components/ErrorModal';

// Mapeo de código de país a emoji de bandera
const getFlagEmoji = (countryCode) => {
  if (!countryCode) return '🏳️';
  // Limpiar y normalizar el código
  const cleanCode = countryCode.trim().toLowerCase();
  if (cleanCode.length !== 2) return '🏳️';
  
  try {
    const codePoints = cleanCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  } catch (e) {
    return '🏳️';
  }
};

// URL para obtener imagen de bandera desde flagcdn
const getFlagImage = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return null;
  return `https://flagcdn.com/w80/${countryCode.toLowerCase()}.png`;
};

// Mapeo de continentes por código de país
const CONTINENT_MAP = {
  // UEFA (Europe)
  'de': 'UEFA', 'es': 'UEFA', 'fr': 'UEFA', 'it': 'UEFA', 'gb': 'UEFA', 'pt': 'UEFA', 'nl': 'UEFA', 'be': 'UEFA',
  'pl': 'UEFA', 'ch': 'UEFA', 'at': 'UEFA', 'dk': 'UEFA', 'se': 'UEFA', 'no': 'UEFA', 'ru': 'UEFA', 'ua': 'UEFA',
  'tr': 'UEFA', 'gr': 'UEFA', 'cz': 'UEFA', 'ro': 'UEFA', 'hr': 'UEFA', 'rs': 'UEFA', 'sk': 'UEFA', 'hu': 'UEFA',
  // CONMEBOL (South America)
  'ar': 'CONMEBOL', 'br': 'CONMEBOL', 'uy': 'CONMEBOL', 'cl': 'CONMEBOL', 'co': 'CONMEBOL', 'pe': 'CONMEBOL',
  'ec': 'CONMEBOL', 'py': 'CONMEBOL', 've': 'CONMEBOL', 'bo': 'CONMEBOL',
  // CONCACAF (North/Central America & Caribbean)
  'us': 'CONCACAF', 'mx': 'CONCACAF', 'ca': 'CONCACAF', 'cr': 'CONCACAF', 'pa': 'CONCACAF', 'hn': 'CONCACAF',
  'gt': 'CONCACAF', 'sv': 'CONCACAF', 'jm': 'CONCACAF', 'tt': 'CONCACAF',
  // AFC (Asia)
  'jp': 'AFC', 'kr': 'AFC', 'cn': 'AFC', 'ir': 'AFC', 'sa': 'AFC', 'ae': 'AFC', 'qa': 'AFC', 'iq': 'AFC',
  'au': 'AFC', 'th': 'AFC', 'vn': 'AFC', 'in': 'AFC', 'kw': 'AFC', 'jo': 'AFC', 'uz': 'AFC',
  // CAF (Africa)
  'eg': 'CAF', 'ng': 'CAF', 'ma': 'CAF', 'sn': 'CAF', 'tn': 'CAF', 'gh': 'CAF', 'cm': 'CAF', 'dz': 'CAF',
  'za': 'CAF', 'ci': 'CAF', 'ml': 'CAF', 'ke': 'CAF',
  // OFC (Oceania)
  'nz': 'OFC', 'fj': 'OFC', 'to': 'OFC', 'ws': 'OFC', 'pg': 'OFC'
};

const getContinent = (flagCode) => {
  return CONTINENT_MAP[flagCode?.toLowerCase()] || 'N/A';
};

export default function Countries() {
  const [countries, setCountries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', flag_code: '' });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const countriesPerPage = 8;

  // Error modal state
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: 'Error',
    message: ''
  });

  const load = () => { 
    api.get('/countries').then(r => setCountries(r.data)).finally(() => setLoading(false)); 
  };
  useEffect(load, []);

  const handleSave = async () => {
    if (!form.name || !form.code || !form.flag_code) { 
      setErrorModal({
        open: true,
        title: 'Campos requeridos faltantes',
        message: 'Por favor, completa los campos: Nombre oficial, Código (3 letras) y Código bandera ISO (2 letras).'
      });
      return; 
    }
    try {
      if (editing) {
        await api.put(`/countries/${editing}`, form);
        toast.success('País actualizado');
      } else {
        await api.post('/countries', form);
        toast.success('País creado');
      }
      setShowForm(false); 
      setEditing(null); 
      setForm({ name: '', code: '', flag_code: '' }); 
      load();
    } catch (err) { 
      const errorMessage = err.response?.data?.detail || 'Error al guardar país';
      setErrorModal({
        open: true,
        title: editing ? 'Error al actualizar país' : 'Error al crear país',
        message: errorMessage
      });
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm('¿Estás seguro de eliminar este país?')) return;
    
    try {
      await api.delete(`/countries/${editing}`);
      toast.success('País eliminado');
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', code: '', flag_code: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleEdit = (c) => { 
    setEditing(c.id); 
    setForm({ name: c.name, code: c.code, flag_code: c.flag_code }); 
    setShowForm(true); 
  };

  // Paginación
  const totalPages = Math.ceil(countries.length / countriesPerPage);
  const startIndex = (currentPage - 1) * countriesPerPage;
  const paginatedCountries = countries.slice(startIndex, startIndex + countriesPerPage);

  // Generar números de página
  const pageNumbers = useMemo(() => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage, '...', totalPages);
      }
    }
    return pages;
  }, [currentPage, totalPages]);

  return (
    <div className="space-y-4 animate-fade-in p-4 lg:p-6" data-testid="admin-countries-page">
      {/* Título y botón crear */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black gold-text" style={{ fontFamily: 'Montserrat' }}>
          Listado de Países
        </h2>
        <button 
          onClick={() => { 
            setEditing(null); 
            setForm({ name: '', code: '', flag_code: '' }); 
            setShowForm(true); 
          }} 
          className="gold-btn px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2" 
          data-testid="create-country-button"
        >
          Crear
          <Plus size={18} />
        </button>
      </div>

      {loading ? (
        <div className="text-center text-white/50 py-10">Cargando...</div>
      ) : (
        <>
          {/* Cards individuales para Desktop y Mobile */}
          <div className="space-y-4">
            {/* Header de columnas */}
            <div className="grid grid-cols-4 gap-2 lg:gap-4 px-4 lg:px-6 py-3 glass-card-subtle text-xs lg:text-sm">
              <div className="text-[#F4E39E] font-bold">ID</div>
              <div className="text-[#F4E39E] font-bold">País</div>
              <div className="text-[#F4E39E] font-bold text-center">Bandera</div>
              <div className="text-[#F4E39E] font-bold text-center">Continente</div>
            </div>

              {/* Cards de países */}
              {paginatedCountries.map((c, idx) => (
                <div 
                  key={c.id} 
                  className="glass-card p-4 lg:p-6 hover:border-[#F4E39E]/50 transition-colors cursor-pointer"
                  onClick={() => handleEdit(c)}
                  data-testid={`country-row-${c.code}`}
                >
                  {/* Desktop Layout */}
                  <div className="hidden lg:grid lg:grid-cols-4 gap-4 items-center">
                    <div className="text-white/90 font-medium">
                      {String(idx + 1 + startIndex).padStart(3, '0')}
                    </div>
                    <div className="text-white/90 font-medium">{c.name}</div>
                    <div className="flex items-center justify-center">
                      {c.flag_code && getFlagImage(c.flag_code) ? (
                        <img 
                          src={getFlagImage(c.flag_code)} 
                          alt={c.code}
                          className="w-16 h-11 object-cover rounded shadow-md"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'inline-block';
                          }}
                        />
                      ) : null}
                      <span 
                        className="text-4xl hidden" 
                        style={{ 
                          fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, Android Emoji, sans-serif',
                          lineHeight: 1
                        }}
                      >
                        {getFlagEmoji(c.flag_code)}
                      </span>
                    </div>
                    <div className="text-center text-white/70">
                      {getContinent(c.flag_code)}
                    </div>
                  </div>

                  {/* Mobile Layout - 4 columnas horizontales */}
                  <div className="lg:hidden grid grid-cols-4 gap-2 items-center text-xs">
                    <div className="text-white/90">{String(idx + 1 + startIndex).padStart(3, '0')}</div>
                    <div className="text-white/90">{c.name}</div>
                    <div className="flex items-center justify-center">
                      {c.flag_code && getFlagImage(c.flag_code) ? (
                        <img 
                          src={getFlagImage(c.flag_code)} 
                          alt={c.code}
                          className="w-10 h-7 object-cover rounded shadow-md"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'inline-block';
                          }}
                        />
                      ) : null}
                      <span 
                        className="text-2xl hidden" 
                        style={{ 
                          fontFamily: 'Apple Color Emoji, Segoe UI Emoji, sans-serif',
                          lineHeight: 1
                        }}
                      >
                        {getFlagEmoji(c.flag_code)}
                      </span>
                    </div>
                    <div className="text-center text-white/70 text-xs">
                      {getContinent(c.flag_code)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 text-[#F4E39E] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              {pageNumbers.map((num, idx) => (
                <button
                  key={idx}
                  onClick={() => typeof num === 'number' && setCurrentPage(num)}
                  disabled={num === '...'}
                  className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                    num === currentPage
                      ? 'bg-[#926F39] text-white'
                      : num === '...'
                      ? 'text-white/50 cursor-default'
                      : 'text-[#F4E39E] hover:bg-white/10'
                  }`}
                >
                  {num}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 text-[#F4E39E] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#111A30] border-[#926F39]/30 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="gold-text" style={{ fontFamily: 'Montserrat' }}>
              {editing ? 'Editar País' : 'Crear País'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-white/60 text-sm">Nombre oficial</label>
              <input 
                value={form.name} 
                onChange={e => setForm({ ...form, name: e.target.value })} 
                data-testid="country-name-input"
                className="w-full bg-[#0A0F1C] border border-[#926F39]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" 
                placeholder="México" 
              />
            </div>
            <div>
              <label className="text-white/60 text-sm">Código (3 letras)</label>
              <input 
                value={form.code} 
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} 
                maxLength={3} 
                data-testid="country-code-input"
                className="w-full bg-[#0A0F1C] border border-[#926F39]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" 
                placeholder="MEX" 
              />
            </div>
            <div>
              <label className="text-white/60 text-sm">Código bandera ISO (2 letras)</label>
              <input 
                value={form.flag_code} 
                onChange={e => setForm({ ...form, flag_code: e.target.value.toLowerCase() })} 
                maxLength={2} 
                data-testid="country-flag-input"
                className="w-full bg-[#0A0F1C] border border-[#926F39]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" 
                placeholder="mx" 
              />
              {form.flag_code && (
                <div className="mt-2 flex items-center gap-3 p-3 bg-[#0A0F1C]/50 rounded-lg border border-[#926F39]/30">
                  <div className="flex items-center justify-center w-20 h-14 bg-[#111A30] rounded-lg overflow-hidden">
                    {getFlagImage(form.flag_code) ? (
                      <img 
                        src={getFlagImage(form.flag_code)} 
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <span 
                      className="text-4xl hidden" 
                      style={{ 
                        fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, Android Emoji, sans-serif',
                        lineHeight: 1
                      }}
                    >
                      {getFlagEmoji(form.flag_code)}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="text-white/50 text-xs">Preview</div>
                    <div className="text-white/80 font-medium">{getContinent(form.flag_code)}</div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button 
                onClick={handleSave} 
                className="gold-btn flex-1 py-3 rounded-lg font-bold" 
                data-testid="save-country-button"
              >
                Guardar
              </button>
              {editing && (
                <button 
                  onClick={handleDelete}
                  className="p-3 rounded-lg border-2 border-red-500/50 text-red-400 hover:bg-red-500/20 transition-colors"
                  title="Eliminar país"
                >
                  <Trash2 size={20} />
                </button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <ErrorModal
        open={errorModal.open}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
}
